// Shopify owns consent/delivery. Only empty, allowlisted funnel signals are published.
(() => {
  if (window.KiddoWonderFunnel) return;
  const names = new Set(['book_selected', 'preview_started', 'preview_generated', 'personalized_preview_viewed']);
  const seen = new Set();
  const watchers = new WeakMap();
  const emit = (name, key) => {
    try {
      if (!names.has(name)) return;
      const id = key ? name + ':' + key : null;
      if (id && seen.has(id)) return;
      const analytics = window.Shopify?.analytics;
      if (typeof analytics?.publish !== 'function') return;
      if (id) {
        seen.add(id);
        if (seen.size > 1000) seen.delete(seen.values().next().value);
      }
      Promise.resolve(analytics.publish('kiddowonder:' + name, {})).catch(() => {});
    } catch (_) { /* Measurement cannot interrupt a preview. */ }
  };
  const viewed = (image, key) => {
    try {
      if (!image || !key || !window.IntersectionObserver) return;
      watchers.get(image)?.();
      let visible = false;
      const cleanup = () => {
        observer.disconnect();
        image.removeEventListener('load', check);
        document.removeEventListener('visibilitychange', check);
      };
      const check = () => {
        if (visible && document.visibilityState === 'visible' && image.complete && image.naturalWidth > 0 && !image.hidden) {
          emit('personalized_preview_viewed', key);
          cleanup();
        }
      };
      const observer = new window.IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.25);
        check();
      }, { threshold: 0.25 });
      watchers.set(image, cleanup);
      image.addEventListener('load', check);
      document.addEventListener('visibilitychange', check);
      observer.observe(image);
    } catch (_) { /* Unsupported browser or observer must not affect the UI. */ }
  };
  window.KiddoWonderFunnel = { emit, viewed };
})();

(function () {
  const PREDEFINED_TALE_BRIEFS = {
    'asteroid-adventure': 'Create "{child_name} and the Asteroid Adventure," a courageous space journey. The opening scenes show {child_name} safely admiring a swirling black hole, racing beside a glittering comet, and discovering glowing crystals on a tiny planet.',
    'princess-name': 'Create "Princess {child_name} and the Rose Court," a royal garden adventure about kindness and courage. The opening scenes show {child_name} finding a tiny spark among the roses, walking the fountain path with friendly animals, and helping a hedgehog in a topiary maze.',
    'backyard-of-wonders': 'Create "{child_name} and the Backyard of Wonders," where an ordinary backyard becomes magical through curiosity. The opening scenes show morning sunshine inviting {child_name} outside, nodding daisies and sweet grass, and the discovery of smooth pebbles and a heart-shaped leaf.',
    'dino-valley-adventure': 'Create "{child_name} in Dino Valley," a moonlit dinosaur adventure about bravery and teamwork. The opening scenes show friendly dinosaurs knocking at the window, a doorway of glowing leaves opening into the jungle, and a waterfall slowing because the magical Moonstone is missing.',
    'name-explores-jobs': 'Create "{child_name} Explores Jobs," a warm career-discovery adventure. The opening scenes show {child_name} feeding chickens and watering vegetables on a farm, measuring and decorating cupcakes in a bakery, and sorting colorful books in a sunlit children\u2019s library.',
    'when-name-grows-up': 'Create "When {child_name} Grows Up," an encouraging journey through dream careers. The opening scenes show {child_name} packing a notebook to try many jobs, learning patience while caring for a farm, and carefully measuring and baking a warm tray of cupcakes.',
    'name-s-birthday-surprise': 'Create "{child_name}\u2019s Birthday Surprise," a joyful celebration filled with family love. The opening scenes show {child_name} waking with birthday excitement, hearing secret giggles and wrapping paper from the garden, and discovering balloons, ribbons, presents, and a beautiful party table.',
    'the-window-to-wonder': 'Create "{child_name} and the Magic Window," a gentle fantasy about imagination. The opening scenes show a mysterious glow outside the evening window, the window becoming a doorway to luminous meadows, and {child_name} drawing a glowing heart that floats among the flowers.',
    'name-s-birthday-magic': 'Create "Happy Birthday, {child_name}!" as a magical birthday keepsake. The opening scenes show {child_name} waking with excitement, tiptoeing downstairs while secret preparations rustle in the garden, and seeing a bright cake and colorful balloons waiting at the celebration.',
    'the-hero-within': 'Create "{child_name} and the Hero Within," a superhero adventure about calm, kind courage. The opening scenes show {child_name} finding a shimmering red cape, feeling quietly strong while tying it on, and discovering a mysterious stone doorway carrying the sound of wings.',
    'clockwork-time-machine': 'Create "{child_name} and the Clockwork Time Machine," a playful journey about time, choices, and discovery. The opening scenes show a magnificent brass machine filled with glowing gears, {child_name} turning a golden time dial inside its cozy cabin, and a portal opening onto a wondrous historical town.',
    'time-travel-compass': 'Create "{child_name} and the Time-Travel Compass," a magical journey guided by courage and curiosity. The opening scenes show an antique compass glowing in a quiet attic, the compass leading {child_name} to an ancient harbor of explorers and maps, and its needle turning again beneath a starry sky.',
    'saint-patrick-s-day': 'Create "Happy Saint Patrick\u2019s Day, {child_name}!" as a cheerful holiday adventure. The opening scenes show {child_name} exploring a quiet town, finding a tall glowing door decorated with two golden keys, and meeting a kind old guide behind the magical doorway.',
    'djali-ne-token-e-shqiponjes': 'Create "{child_name} n\u00eb Tok\u00ebn e Shqiponj\u00ebs," an Albanian heritage adventure for a boy. The opening scenes show {child_name} wondering what it means to be Albanian, a majestic golden eagle arriving at the moonlit window, and a flight above mountains, rivers, castles, songs, and red-and-black flags.',
    'vajza-ne-token-e-shqiponjes': 'Create "{child_name} n\u00eb Tok\u00ebn e Shqiponj\u00ebs," an Albanian heritage adventure for a girl. The opening scenes show {child_name} wondering what it means to be Albanian, a wise golden eagle arriving at the moonlit window, and a flight above mountains, rivers, ancient castles, and proud red-and-black flags.',
    'mira-and-the-three-coins': 'Create "{child_name} and the Three Coins," a gentle first money story. The opening scenes show {child_name} discovering gold, silver, and copper coins, imagining a small treat, and learning that one coin can be spent, one saved, and one shared with kindness.',
    'niks-first-big-idea': 'Create "{child_name}\u2019s First Big Idea," a warm problem-solving story. The opening scenes show {child_name} searching through a messy playroom, noticing that toys need clear homes, and organizing cars, blocks, books, and a tiny robot into picture-labeled boxes.',
    'miras-smart-choice': 'Create "{child_name}\u2019s Smart Choice," a gentle story about long-term value. The opening scenes show {child_name} choosing among a sparkly sticker, a picture book, and flower seeds, imagining what each choice could become, and patiently planting the seeds.',
    'niks-toy-repair-shop': 'Create "{child_name}\u2019s Toy Repair Shop," a caring repair-and-reuse story. The opening scenes show a toy car with a loose wheel, a robot whose light will not blink, and a teddy bear with a loose bow before {child_name} opens a tiny repair station.',
    'niks-helpful-cart': 'Create "{child_name}\u2019s Helpful Cart," a cheerful service story. The opening scenes show {child_name} turning a toy wagon into a delivery cart, learning to load it carefully, and bringing a car, book, teddy bear, and robot to the right places.',
    'miras-sunny-lemonade-stand': 'Create "{child_name}\u2019s Sunny Lemonade Stand," a bright story about testing and improving an idea. The opening scenes show {child_name} squeezing lemons, tasting a mix that is too sour and then too sweet, and adjusting it until the lemonade is just right.',
    'miras-brave-bell': 'Create "{child_name}\u2019s Brave Bell," a gentle American tale inspired by the Liberty Bell. The opening scenes show {child_name} discovering the historic bell, making a tiny brass Liberty Bell, and learning to use a brave little voice to help.',
    'nik-and-the-golden-spike': 'Create "{child_name} and the Golden Spike," a railway-building story about friendship and connection. The opening scenes show two toy trains living far apart, {child_name} building tracks and a bridge between them, and a shiny golden peg joining the route.',
    'miras-liberty-light': 'Create "{child_name}\u2019s Liberty Light," a welcoming American tale inspired by the Statue of Liberty. The opening scenes show {child_name} discovering the green statue and glowing torch, making a little Liberty Light, and guiding a lost paper boat safely into a tiny harbor.',
    'nik-and-the-moon-mission': 'Create "{child_name} and the Moon Mission," a gentle space adventure inspired by the first Moon landing. The opening scenes show {child_name} studying rockets, building a cardboard spacecraft and lunar surface, and preparing to take one brave little Moon step.',
    'miras-many-seeds': 'Create "{child_name}\u2019s Many Seeds," a warm American tale about kindness and belonging. The opening scenes show {child_name} discovering many different seeds, choosing to give each one a place in the garden, and patiently caring for the quiet soil.',
    'niks-starry-kite': 'Create "{child_name}\u2019s Starry Kite," a gentle story inspired by the American flag. The opening scenes show a red, white, and blue kite rising in the park, its ribbons and stars becoming tangled, and {child_name} carefully helping every part find its place.',
    'miras-thankful-table': 'Create "{child_name}\u2019s Thankful Table," a cozy Thanksgiving story about gratitude and sharing. The opening scenes show {child_name} discovering a festive table, arranging a pumpkin, apple, bread, and autumn leaves, and noticing that the table still needs friends.',
    'niks-route-66-adventure': 'Create "{child_name}\u2019s Route 66 Adventure," a playful road-trip story about enjoying the journey. The opening scenes show {child_name} discovering the famous road, building a winding playroom route with roadside stops, and setting off in a small red car.',
    'niks-first-goal': 'Create "{child_name}\u2019s First Goal," a gentle soccer story about trying again. The opening scenes show {child_name} bringing a blue-starred ball to the park, sending early kicks toward flowers and cones, and learning to use calm feet and careful eyes.',
    'mira-and-the-swimming-star': 'Create "{child_name} and the Swimming Star," a gentle swimming story about courage. The opening scenes show {child_name} arriving at a bright pool with a yellow floating star, testing the water one toe at a time, and turning little splashes into confidence.',
    'niks-first-basket': 'Create "{child_name}\u2019s First Basket," a gentle basketball story about patient practice. The opening scenes show {child_name} bouncing a small orange ball, trying shots that bump the rim or roll away, and pausing to breathe, look, and try again.',
    'niks-runaway-shoes': 'Create "{child_name}\u2019s Runaway Shoes," a joyful running story about listening to the body. The opening scenes show bright red shoes tugging {child_name} down a sunny garden path, a fast run becoming tiring, and a pause beneath a shady tree.',
    'miras-magic-gymnastics-ribbon': 'Create "{child_name}\u2019s Magic Gymnastics Ribbon," a colorful story about rhythm and joyful movement. The opening scenes show {child_name} swishing a striped ribbon in the yard, the ribbon flopping and tangling when rushed, and a soft breeze revealing its gentle rhythm.',
    'mira-and-the-idea-jar': 'Create "{child_name} and the Idea Jar," a gentle entrepreneurship story about growing small ideas. The opening scenes show {child_name} turning a shiny empty jar into an Idea Jar, filling it with drawings of a house, road, park, shop, and flower, and choosing the first folded idea to build.',
    'nik-and-the-problem-box': 'Create "{child_name} and the Problem Box," a warm problem-solving adventure. The opening scenes show a blue box with a silver star, a toy car with a missing wheel, and the first paper clue inviting {child_name} to look carefully.',
    'miras-almost-perfect-shop': 'Create "{child_name}\u2019s Almost-Perfect Shop," a playful story about learning from mistakes. The opening scenes show {child_name} arranging handmade shop items, a proud sign slipping to the floor, and small improvements making the shop warmer and more useful.',
    'miras-little-big-plan': 'Create "{child_name}\u2019s Little Big Plan," a gentle story about breaking a big idea into small steps. The opening scenes show a jumble of tiny treasures, {child_name} dreaming of a museum, and a four-step plan labeled Pick, Sort, Build, and Show.',
    'niks-tiny-customer': 'Create "{child_name}\u2019s Tiny Customer," a caring story about understanding what others need. The opening scenes show {child_name} opening a tiny stand, welcoming a wooden bird, and noticing that cookies and drinks are not what the quiet customer needs.',
    'mira-and-the-colorful-price-tags': 'Create "{child_name} and the Colorful Price Tags," a gentle story about value, fairness, and care. The opening scenes show a little shop of paper flowers, bracelets, and handmade cards, colorful tags being assigned, and {child_name} wondering what each creation is truly worth.',
    'nik-and-the-borrowed-toolbox': 'Create "{child_name} and the Borrowed Toolbox," a warm story about responsibility and trust. The opening scenes show {child_name} planning a sign, carefully opening a red-handled borrowed toolbox, and measuring a small wooden board before beginning.',
  };

  const PREDEFINED_TALE_TITLES = {
    'asteroid-adventure': 'Asteroid Adventure',
    'princess-name': 'Princess Tale',
    'backyard-of-wonders': 'Backyard of Wonders',
    'dino-valley-adventure': 'Dino Valley Adventure',
    'name-explores-jobs': 'Name Explores Jobs',
    'when-name-grows-up': 'When Name Grows Up',
    'name-s-birthday-surprise': 'Birthday Surprise',
    'the-window-to-wonder': 'The Window to Wonder',
    'name-s-birthday-magic': 'Birthday Magic',
    'the-hero-within': 'The Hero Within',
    'clockwork-time-machine': 'Clockwork Time Machine',
    'time-travel-compass': 'Time-Travel Compass',
    'saint-patrick-s-day': "Saint Patrick's Day",
    'djali-ne-token-e-shqiponjes': 'Djali në Tokën e Shqiponjës',
    'vajza-ne-token-e-shqiponjes': 'Vajza në Tokën e Shqiponjës',
    'mira-and-the-three-coins': 'Mira and the Three Coins',
    'niks-first-big-idea': "Nik's First Big Idea",
    'miras-smart-choice': "Mira's Smart Choice",
    'niks-toy-repair-shop': "Nik's Toy Repair Shop",
    'niks-helpful-cart': "Nik's Helpful Cart",
    'miras-sunny-lemonade-stand': "Mira's Sunny Lemonade Stand",
    'miras-brave-bell': "Mira's Brave Bell",
    'nik-and-the-golden-spike': 'Nik and the Golden Spike',
    'miras-liberty-light': "Mira's Liberty Light",
    'nik-and-the-moon-mission': 'Nik and the Moon Mission',
    'miras-many-seeds': "Mira's Many Seeds",
    'niks-starry-kite': "Nik's Starry Kite",
    'miras-thankful-table': "Mira's Thankful Table",
    'niks-route-66-adventure': "Nik's Route 66 Adventure",
    'niks-first-goal': "Nik's First Goal",
    'mira-and-the-swimming-star': 'Mira and the Swimming Star',
    'niks-first-basket': "Nik's First Basket",
    'niks-runaway-shoes': "Nik's Runaway Shoes",
    'miras-magic-gymnastics-ribbon': "Mira's Magic Gymnastics Ribbon",
    'mira-and-the-idea-jar': 'Mira and the Idea Jar',
    'nik-and-the-problem-box': 'Nik and the Problem Box',
    'miras-almost-perfect-shop': "Mira's Almost-Perfect Shop",
    'miras-little-big-plan': "Mira's Little Big Plan",
    'niks-tiny-customer': "Nik's Tiny Customer",
    'mira-and-the-colorful-price-tags': 'Mira and the Colorful Price Tags',
    'nik-and-the-borrowed-toolbox': 'Nik and the Borrowed Toolbox',
  };

  function customProduct(root) {
    const element = root.querySelector('[data-custom-product]');
    if (!element) return {};
    try {
      const parsed = JSON.parse(element.textContent || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function findCoverVariants(product) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    const matches = (variant, pattern) => pattern.test(String(variant && variant.title || '').toLowerCase());
    return {
      softcover: variants.find((variant) => matches(variant, /soft[\s-]*cover|softcover|paperback/)) || null,
      hardcover: variants.find((variant) => matches(variant, /hard[\s-]*cover|hardcover|hardback/)) || null,
    };
  }

  function formatStorefrontPrice(price) {
    const cents = Number(price);
    if (!Number.isFinite(cents)) return '';
    const currency = window.Shopify && window.Shopify.currency
      ? (window.Shopify.currency.active || window.Shopify.currency.current)
      : '';
    if (currency) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency,
        }).format(cents / 100);
      } catch (error) {
        // Fall back to a plain amount when the market currency is unavailable.
      }
    }
    return (cents / 100).toFixed(2);
  }

  class KiddoWonderCustomTale {
    constructor(root) {
      if (!root || root.dataset.taleInitialized === 'true') return;
      root.dataset.taleInitialized = 'true';

      this.root = root;
      this.engine = (root.dataset.engineUrl || '').replace(/\/+$/, '');
      const configuredProvider = ['google', 'google_lite', 'flux'].includes(root.dataset.imageProvider)
        ? root.dataset.imageProvider
        : 'openai';
      const requestedBook = (new URL(window.location.href).searchParams.get('book') || '').trim().toLowerCase();
      this.predefinedProduct = {};
      this.isPredefined = Boolean(PREDEFINED_TALE_BRIEFS[requestedBook]);
      this.productSlug = this.isPredefined ? requestedBook : 'custom-tale';
      this.imageModel = ['gpt-image-2.5-flare', 'gpt-image-2.5-sunburst'].includes(root.dataset.imageProvider)
        ? root.dataset.imageProvider : '';
      this.imageProvider = this.isPredefined ? 'openai' : configuredProvider;
      this.purchaseProduct = this.isPredefined ? {} : customProduct(root);
      this.purchaseVariants = findCoverVariants(this.purchaseProduct);
      this.variantId = String(
        this.isPredefined ? (this.predefinedProduct.variantId || 0) : root.dataset.variantId
      ).trim();
      this.productTitle = String(
        this.isPredefined
          ? (this.predefinedProduct.title || PREDEFINED_TALE_TITLES[requestedBook])
          : root.dataset.productTitle
      ).trim() || (this.isPredefined ? this.productSlug.replaceAll('-', ' ') : 'Custom Tale');
      this.productPrice = String(
        this.isPredefined ? (this.predefinedProduct.price || '') : root.dataset.productPrice
      ).trim();
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.formView = root.querySelector('[data-tale-form-view]');
      this.form = root.querySelector('[data-tale-form]');
      this.hero = root.querySelector('[data-tale-hero]');
      this.progressView = root.querySelector('[data-tale-progress]');
      this.previewView = root.querySelector('[data-tale-preview]');
      this.alert = root.querySelector('[data-tale-alert]');
      this.generateButton = root.querySelector('[data-generate-button]');
      this.editButton = root.querySelector('[data-edit-button]');
      this.regenerateButton = root.querySelector('[data-regenerate-button]');
      this.purchaseButtons = Array.from(root.querySelectorAll('[data-purchase-button]'));
      this.purchaseStatus = root.querySelector('[data-purchase-status]');
      this.pagesButton = root.querySelector('[data-pages-button]');
      this.pagesGate = root.querySelector('[data-pages-gate]');
      this.pagesPreview = root.querySelector('[data-pages-preview]');
      this.progressHeading = root.querySelector('[data-progress-heading]');
      this.progressStage = root.querySelector('[data-progress-stage]');
      this.progressPercentage = root.querySelector('[data-progress-percentage]');
      this.progressBar = root.querySelector('[data-progress-bar]');
      this.photoInput = root.querySelector('[data-photo-input]');
      this.photoDropzone = root.querySelector('[data-photo-dropzone]');
      this.photoPreview = root.querySelector('[data-photo-preview]');
      this.photoReady = root.querySelector('[data-photo-ready]');
      this.progressTrack = root.querySelector('[data-progress-track]');
      this.storyKeywordsInput = root.querySelector('[data-story-keywords]');
      this.storyKeywordCount = root.querySelector('[data-story-keyword-count]');
      this.batchId = '';
      this.previewData = null;
      this.pollTimer = null;
      this.pollFailures = 0;
      this.maxPollFailures = 4;
      this.progressValue = 0;
      this.progressTarget = 0;
      this.progressTimer = null;
      this.progressStageText = '';
      this.progressSummarySignature = '';
      this.progressSummaryTimer = null;
      this.progressSummaryIndex = 0;
      this.photoObjectUrl = '';
      this.photoFile = null;
      this.clientId = '';
      this.lastPreviewSignature = '';
      this.purchaseProductLoading = this.isPredefined;
      this.purchaseProductError = '';
      this.purchaseProductPromise = Promise.resolve(this.purchaseProduct);
      this.batchStorageKey = `kw_custom_tale_batch_${this.productSlug}_${this.imageProvider}${this.imageModel ? '_' + this.imageModel : ''}`;

      this.configureMode();
      this.bindEvents();
      window.KiddoWonderFunnel?.emit('book_selected', this.productSlug);
      this.restoreBatch();
      if (this.isPredefined) {
        this.purchaseProductPromise = this.loadPredefinedPurchaseProduct();
      }
    }

    configureMode() {
      this.root.classList.toggle('kw-tale--predefined', this.isPredefined);
      this.root.dataset.builderMode = this.isPredefined ? 'predefined' : 'custom';
      this.root.querySelectorAll('[data-custom-only]').forEach((element) => {
        element.hidden = this.isPredefined;
      });
      this.root.querySelectorAll('[data-predefined-only]').forEach((element) => {
        element.hidden = !this.isPredefined;
      });

      const storyRequest = this.form.querySelector('[name="story_request"]');
      if (storyRequest) {
        storyRequest.required = false;
        if (this.isPredefined) {
          // Backward-compatible fallback for a Render deployment that predates
          // product_slug routing. The updated backend ignores this field and
          // uses its stricter fixed plan; the older backend uses this exact,
          // predetermined brief instead of rejecting the request as empty.
          storyRequest.value = `${PREDEFINED_TALE_BRIEFS[this.productSlug]} Keep the same child recognizable from the uploaded reference photo in every image. Use a premium warm magical storybook illustration style. Keep all content child-safe and suitable for ages 3 to 8.`;
        } else {
          storyRequest.value = '';
        }
      }
      if (this.storyKeywordsInput) {
        this.storyKeywordsInput.disabled = this.isPredefined;
        this.storyKeywordsInput.required = !this.isPredefined;
      }
      this.updateStoryKeywordCount();

      const customConfiguration = this.root.querySelector('[data-custom-configuration]');
      const predefinedConfiguration = this.root.querySelector('[data-predefined-configuration]');
      const hasPurchaseOptions = Boolean(this.purchaseVariants.softcover && this.purchaseVariants.hardcover);
      if (customConfiguration) customConfiguration.hidden = this.isPredefined || hasPurchaseOptions;
      if (predefinedConfiguration) {
        predefinedConfiguration.hidden = !this.isPredefined || hasPurchaseOptions || this.purchaseProductLoading;
      }

      if (this.isPredefined) {
        const content = {
          '[data-progress-eyebrow]': 'Creating your personalized preview',
        };
        Object.entries(content).forEach(([selector, text]) => {
          const element = this.root.querySelector(selector);
          if (element) element.textContent = text;
        });
        this.hero.hidden = true;
        const storyCard = this.form.querySelector('.kw-tale__card--story');
        if (storyCard) storyCard.setAttribute('aria-label', 'Create personalized preview');
      }

      this.updatePurchaseButtons(false, false, false);
    }

    async loadPredefinedPurchaseProduct() {
      try {
        const response = await fetch(
          this.shopifyRoute(`products/${encodeURIComponent(this.productSlug)}.js`),
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          }
        );
        const product = await response.json().catch(() => ({}));
        if (!response.ok || !product || !product.id) {
          throw new Error(product.description || product.message || 'The story product could not be found.');
        }

        this.predefinedProduct = {
          variantId: product.variants && product.variants[0] ? product.variants[0].id : 0,
          title: product.title || PREDEFINED_TALE_TITLES[this.productSlug] || '',
          price: formatStorefrontPrice(product.price),
          variants: (Array.isArray(product.variants) ? product.variants : []).map((variant) => ({
            id: variant.id,
            title: variant.title,
            price: formatStorefrontPrice(variant.price),
            available: variant.available !== false,
          })),
        };
        this.purchaseProduct = this.predefinedProduct;
        this.purchaseVariants = findCoverVariants(this.purchaseProduct);
        this.variantId = String(this.predefinedProduct.variantId || 0);
        this.productTitle = String(this.predefinedProduct.title || this.productTitle).trim();
        this.productPrice = String(this.predefinedProduct.price || '').trim();
        this.purchaseProductError = '';

        return this.purchaseProduct;
      } catch (error) {
        this.purchaseProduct = {};
        this.purchaseVariants = findCoverVariants(this.purchaseProduct);
        this.purchaseProductError = error && error.message
          ? error.message
          : 'The story product could not be loaded.';
        return null;
      } finally {
        this.purchaseProductLoading = false;
        this.syncPurchaseConfiguration();
      }
    }

    syncPurchaseConfiguration() {
      const customConfiguration = this.root.querySelector('[data-custom-configuration]');
      const predefinedConfiguration = this.root.querySelector('[data-predefined-configuration]');
      const hasPurchaseOptions = Boolean(this.purchaseVariants.softcover && this.purchaseVariants.hardcover);

      if (customConfiguration) {
        customConfiguration.hidden = this.isPredefined || hasPurchaseOptions;
      }
      if (predefinedConfiguration) {
        predefinedConfiguration.hidden = !this.isPredefined || hasPurchaseOptions || this.purchaseProductLoading;
        if (this.isPredefined && !hasPurchaseOptions && !this.purchaseProductLoading) {
          predefinedConfiguration.textContent = this.purchaseProductError
            ? `Store setup required: Shopify could not load the “${this.productSlug}” product. ${this.purchaseProductError}`
            : `Store setup required: add Softcover and Hardcover variants to “${this.productTitle}”.`;
        }
      }

      const hasCover = Boolean(
        this.previewData && this.previewData.cover && this.previewData.cover.image_url
      );
      const pagesRequested = Boolean(this.previewData && this.previewData.pages_requested);
      const ready = Boolean(this.previewData && this.previewData.ready);
      this.updatePurchaseButtons(ready, hasCover, pagesRequested);
    }

    purchaseLabel(coverType) {
      return coverType === 'softcover' ? 'Get the Softcover Book' : 'Get the Hardcover Book';
    }

    updatePurchaseButtons(ready, hasCover, pagesRequested) {
      const missing = [];
      this.purchaseButtons.forEach((button) => {
        const coverType = button.dataset.coverType;
        const variant = this.purchaseVariants[coverType];
        const available = Boolean(variant && variant.id && variant.available !== false);
        const action = button.querySelector('[data-purchase-action]');
        const price = button.querySelector('[data-purchase-price]');
        if (action) action.textContent = this.purchaseLabel(coverType);
        if (price && variant && variant.price) price.textContent = variant.price;
        button.dataset.variantId = available ? String(variant.id) : '';
        button.disabled = !hasCover || !available;
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
        if (!available) missing.push(coverType);
      });

      if (!this.purchaseStatus) return;
      if (missing.length) {
        const labels = missing.map((type) => type === 'softcover' ? 'Softcover' : 'Hardcover');
        this.purchaseStatus.textContent = `${labels.join(' and ')} ${labels.length === 1 ? 'option is' : 'options are'} being configured.`;
      } else if (hasCover) {
        this.purchaseStatus.textContent = 'You will get the full ebook after you purchase so that you confirm every page and then we will print';
      } else {
        this.purchaseStatus.textContent = 'Purchase options appear as soon as your cover is ready.';
      }
    }

    storyKeywords() {
      const value = this.storyKeywordsInput ? String(this.storyKeywordsInput.value || '').trim() : '';
      return value ? value.split(/[\s,]+/).filter(Boolean) : [];
    }

    updateStoryKeywordCount() {
      const keywordCount = this.storyKeywords().length;
      const overLimit = keywordCount > 5;
      if (this.storyKeywordCount) {
        this.storyKeywordCount.textContent = String(keywordCount);
        const counter = this.storyKeywordCount.closest('.kw-tale__keyword-count');
        if (counter) counter.classList.toggle('is-over-limit', overLimit);
      }
      if (this.storyKeywordsInput) {
        this.storyKeywordsInput.setCustomValidity(
          overLimit ? 'Please use no more than 5 story keywords.' : ''
        );
        this.storyKeywordsInput.setAttribute('aria-invalid', overLimit ? 'true' : 'false');
      }
    }

    bindEvents() {
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.photoInput.addEventListener('change', () => this.showPhoto());
      if (this.storyKeywordsInput) {
        this.storyKeywordsInput.addEventListener('input', () => this.updateStoryKeywordCount());
      }
      this.regenerateButton.addEventListener('click', () => this.regenerate());
      this.pagesButton.addEventListener('click', () => this.generatePages());
      this.editButton.addEventListener('click', () => this.edit());
      this.purchaseButtons.forEach((button) => {
        button.addEventListener('click', () => this.addToCart(button.dataset.coverType, button));
      });

      ['dragenter', 'dragover'].forEach((name) => {
        this.photoDropzone.addEventListener(name, (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
          this.photoDropzone.classList.add('is-dragging');
        });
      });
      ['dragleave', 'dragend'].forEach((name) => {
        this.photoDropzone.addEventListener(name, () => this.photoDropzone.classList.remove('is-dragging'));
      });
      this.photoDropzone.addEventListener('drop', (event) => this.useDroppedPhoto(event));
    }

    apiUrl(path) {
      if (!this.engine) throw new Error('The Custom Tale engine URL is not configured.');
      return new URL(path.replace(/^\//, ''), `${this.engine}/`).toString();
    }

    async fetchJson(url, options) {
      let response;
      try {
        response = await fetch(url, options || {});
      } catch (cause) {
        const error = new Error('We could not reach the preview service. Please check your connection and try again.');
        error.retryable = true;
        error.cause = cause;
        throw error;
      }
      const data = await response.json().catch(() => null);
      if (!data || typeof data !== 'object') {
        const message = response.status === 404
          ? 'This preview option is temporarily unavailable. Please try again shortly.'
          : `The preview service returned an invalid response (${response.status}).`;
        const error = new Error(message);
        error.retryable = response.ok || response.status >= 500;
        throw error;
      }
      if (!response.ok || data.error) {
        const error = new Error(data.error || data.message || `Request failed (${response.status})`);
        error.retryable = response.status === 408 || response.status === 425 || response.status === 429 || response.status >= 500;
        throw error;
      }
      return data;
    }

    useDroppedPhoto(event) {
      event.preventDefault();
      event.stopPropagation();
      this.photoDropzone.classList.remove('is-dragging');
      const droppedFiles = event.dataTransfer && event.dataTransfer.files;
      const file = droppedFiles && droppedFiles[0];
      if (!file) return;

      if (droppedFiles.length !== 1) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('Please drop one child photo at a time.');
        this.photoInput.focus();
        return;
      }

      try {
        const transfer = new DataTransfer();
        transfer.items.add(file);
        this.photoInput.files = transfer.files;
      } catch (error) {
        try {
          this.photoInput.files = droppedFiles;
        } catch (ignored) {
          // The explicit File stored below is sufficient for submission.
        }
      }

      // The stored File is also placed directly into FormData during submit,
      // so drag-and-drop still works when a browser blocks assigning input.files.
      this.showPhoto(file);
    }

    resetPhoto() {
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = '';
      this.photoFile = null;
      this.photoInput.value = '';
      this.photoPreview.onload = null;
      this.photoPreview.onerror = null;
      this.photoPreview.removeAttribute('src');
      this.photoPreview.hidden = true;
      this.photoDropzone.classList.remove('has-photo');
      this.photoReady.hidden = true;
    }

    markPhotoInvalid() {
      this.photoInput.setAttribute('aria-invalid', 'true');
      if (this.alert.id) this.photoInput.setAttribute('aria-errormessage', this.alert.id);
      this.photoDropzone.classList.add('is-invalid');
    }

    markPhotoValid() {
      this.photoInput.removeAttribute('aria-invalid');
      this.photoInput.removeAttribute('aria-errormessage');
      this.photoDropzone.classList.remove('is-invalid');
    }

    showPhoto(selectedFile) {
      const file = selectedFile || (this.photoInput.files && this.photoInput.files[0]);
      if (!file) return;
      if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('Please choose a JPG, PNG, or WebP photo.');
        this.photoInput.focus();
        return;
      }
      if (file.size > 15 * 1024 * 1024) {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('The photo is too large. Please choose an image under 15 MB.');
        this.photoInput.focus();
        return;
      }
      if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl);
      this.photoFile = file;
      this.photoObjectUrl = URL.createObjectURL(file);
      this.photoPreview.hidden = true;
      this.photoDropzone.classList.remove('has-photo');
      this.photoReady.hidden = true;
      this.photoPreview.onload = () => {
        this.photoPreview.hidden = false;
        this.photoDropzone.classList.add('has-photo');
        this.photoReady.hidden = false;
      };
      this.photoPreview.onerror = () => {
        this.resetPhoto();
        this.markPhotoInvalid();
        this.showAlert('We could not read that photo. Please choose another JPG, PNG, or WebP image.');
      };
      this.photoPreview.src = this.photoObjectUrl;
      this.markPhotoValid();
      this.clearAlert();
    }

    scrollBehavior() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    showAlert(message) {
      this.alert.textContent = message || 'Something went wrong. Please try again.';
      this.alert.hidden = false;
      this.alert.scrollIntoView({ behavior: this.scrollBehavior(), block: 'center' });
    }

    clearAlert() {
      this.alert.hidden = true;
      this.alert.textContent = '';
    }

    setView(view) {
      const targetView = view === 'form' ? this.formView : view === 'progress' ? this.progressView : this.previewView;
      const shouldScroll = targetView.hidden;
      this.hero.hidden = view !== 'form' || this.isPredefined;
      this.formView.hidden = view !== 'form';
      this.progressView.hidden = view !== 'progress';
      this.previewView.hidden = view !== 'preview';
      this.root.setAttribute('aria-busy', view === 'progress' ? 'true' : 'false');
      if (view !== 'progress') {
        this.stopProgressTicker();
        this.stopProgressSummary();
      }
      if (view !== 'form' && shouldScroll) {
        this.root.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
      }
    }

    renderProgress(progress, stage) {
      const safeProgress = Math.max(0, Math.min(100, Number(progress || 0)));
      const displayedProgress = Math.floor(safeProgress);
      this.progressValue = safeProgress;
      this.progressBar.style.width = `${safeProgress}%`;
      if (this.progressPercentage) this.progressPercentage.textContent = `${displayedProgress}%`;
      this.progressStage.textContent = stage || 'Creating your preview...';
      if (this.progressTrack) {
        this.progressTrack.setAttribute('aria-valuenow', String(displayedProgress));
        this.progressTrack.setAttribute('aria-valuetext', `${displayedProgress}% — ${stage || 'Creating your preview'}`);
      }
    }

    progressSummaryMessages(summary) {
      const clean = String(summary || '').replace(/\s+/g, ' ').trim();
      if (!clean) return [];

      const sentences = clean.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [clean];
      return sentences
        .map((sentence) => sentence.trim())
        .filter(Boolean)
        .slice(0, 5);
    }

    stopProgressSummary() {
      if (!this.progressSummaryTimer) return;
      window.clearInterval(this.progressSummaryTimer);
      this.progressSummaryTimer = null;
    }

    showProgressSummaryMessage(messages) {
      if (!this.progressHeading || !messages.length) return;
      this.progressHeading.classList.remove('is-entering');
      void this.progressHeading.offsetWidth;
      this.progressHeading.textContent = messages[this.progressSummaryIndex];
      this.progressHeading.classList.add('is-entering');
    }

    setProgressSummary(summary) {
      const messages = this.progressSummaryMessages(summary);
      if (!this.progressHeading || !messages.length) return;

      const signature = messages.join('\n');
      if (signature === this.progressSummarySignature) return;

      this.stopProgressSummary();
      this.progressSummarySignature = signature;
      this.progressSummaryIndex = 0;
      this.showProgressSummaryMessage(messages);

      if (messages.length > 1) {
        this.progressSummaryTimer = window.setInterval(() => {
          this.progressSummaryIndex = (this.progressSummaryIndex + 1) % messages.length;
          this.showProgressSummaryMessage(messages);
        }, 8000);
      }
    }

    progressCeiling(progress, stage) {
      if (progress >= 100) return 100;
      const label = String(stage || '').toLowerCase();
      if (label.includes('writing') || label.includes('story')) return Math.max(progress, 30);
      if (label.includes('painting') || label.includes('cover') || label.includes('refreshing')) return Math.max(progress, 94);
      return Math.max(progress, Math.min(94, progress + 10));
    }

    startProgressTicker() {
      if (this.progressTimer) return;
      this.progressTimer = window.setInterval(() => {
        if (this.progressValue >= this.progressTarget || this.progressValue >= 99) return;
        const remaining = this.progressTarget - this.progressValue;
        const step = remaining > 35 ? 0.72 : remaining > 12 ? 0.48 : 0.28;
        this.renderProgress(Math.min(this.progressTarget, this.progressValue + step), this.progressStageText);
      }, 850);
    }

    stopProgressTicker() {
      if (!this.progressTimer) return;
      window.clearInterval(this.progressTimer);
      this.progressTimer = null;
    }

    resetProgress() {
      this.stopProgressTicker();
      this.stopProgressSummary();
      this.progressValue = 0;
      this.progressTarget = 0;
      this.progressStageText = '';
      this.progressSummarySignature = '';
      this.progressSummaryIndex = 0;
      if (this.progressHeading) {
        this.progressHeading.classList.remove('is-entering');
        this.progressHeading.textContent = 'Your personalized story is taking shape.';
      }
      this.renderProgress(0, 'Writing your story...');
    }

    setProgress(progress, stage) {
      const reportedProgress = Math.max(0, Math.min(100, Number(progress || 0)));
      this.progressStageText = stage || 'Creating your preview...';
      if (reportedProgress >= 100) {
        this.progressTarget = 100;
        this.renderProgress(100, this.progressStageText);
        this.stopProgressTicker();
        return;
      }

      const minimumVisibleProgress = Math.max(this.progressValue, reportedProgress);
      this.progressTarget = Math.max(minimumVisibleProgress, this.progressCeiling(reportedProgress, this.progressStageText));
      this.renderProgress(minimumVisibleProgress, this.progressStageText);
      this.startProgressTicker();
    }

    resetPreview() {
      this.previewData = null;
      this.lastPreviewSignature = '';
      const coverImage = this.root.querySelector('[data-cover-image]');
      coverImage.onload = null;
      coverImage.onerror = null;
      coverImage.removeAttribute('src');
      coverImage.removeAttribute('data-loaded-url');
      coverImage.hidden = true;

      this.root.querySelectorAll('[data-page-card]').forEach((card) => card.classList.remove('is-ready'));
      this.root.querySelectorAll('[data-page-image]').forEach((image) => {
        image.onload = null;
        image.onerror = null;
        image.removeAttribute('src');
        image.removeAttribute('data-loaded-url');
        image.hidden = true;
      });
      this.root.querySelectorAll('[data-page-loading]').forEach((loading) => { loading.hidden = false; });
      this.root.querySelectorAll('[data-page-copy]').forEach((copy) => { copy.hidden = true; });
      this.pagesGate.hidden = true;
      this.pagesPreview.hidden = true;
      this.pagesButton.disabled = false;
      this.pagesButton.textContent = 'See the first pages';
      this.setPreviewControls(false, false, false);
    }

    publishAnalytics(eventName, eventData) {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return false;
      try {
        const published = analytics.publish(`kiddowonder:${eventName}`, eventData || {});
        if (published && typeof published.catch === 'function') published.catch(() => {});
        return true;
      } catch (error) {
        // Tracking must never interrupt the customer's book-building flow.
        return false;
      }
    }

    publishAnalyticsOnce(eventName, eventData) {
      const storageKey = `kw_analytics_${eventName}_${this.batchId || 'pending'}`;
      try {
        if (window.sessionStorage.getItem(storageKey) === '1') return;
      } catch (error) {
        // Continue with in-page tracking when browser storage is unavailable.
      }
      if (!this.publishAnalytics(eventName, eventData)) return;
      try {
        window.sessionStorage.setItem(storageKey, '1');
      } catch (error) {
        // The event was still published when browser storage is unavailable.
      }
    }

    setPreviewControls(ready, hasCover, pagesRequested) {
      this.updatePurchaseButtons(ready, hasCover, pagesRequested);
      this.regenerateButton.disabled = !hasCover || Boolean(pagesRequested && !ready);
      this.root.setAttribute('aria-busy', pagesRequested && !ready ? 'true' : 'false');
    }

    previewSignature(data) {
      const readyPages = Array.isArray(data.pages)
        ? data.pages.filter((page) => page.ready).map((page) => Number(page.page || 0)).sort().join(',')
        : String(data.pages_ready || 0);
      return `${data.cover_ready || data.cover_url ? 'cover' : 'waiting'}|${data.pages_requested ? 'pages' : 'cover-only'}|${readyPages}|${data.ready ? 'ready' : 'working'}`;
    }

    isValidBatch(batchId) {
      return /^[a-f0-9]{32}$/i.test(String(batchId || ''));
    }

    storeBatch(batchId) {
      try {
        if (batchId) window.sessionStorage.setItem(this.batchStorageKey, batchId);
        else window.sessionStorage.removeItem(this.batchStorageKey);
      } catch (error) {
        // Storage can be disabled in privacy modes; the current page still works.
      }
    }

    clearCustomBatchFromUrl() {
      const url = new URL(window.location.href);
      const batch = url.searchParams.get('batch');
      if (!this.isValidBatch(batch)) return;
      url.searchParams.delete('batch');
      window.history.replaceState({}, '', url.toString());
    }

    restoreBatch() {
      const urlBatch = new URL(window.location.href).searchParams.get('batch');
      let storedBatch = '';
      try {
        storedBatch = window.sessionStorage.getItem(this.batchStorageKey) || '';
      } catch (error) {
        storedBatch = '';
      }

      // Shopify's Theme Editor can use its own `batch` query parameter.
      // Custom Tale batches are always UUID4 hex values created by Render.
      const batch = this.isValidBatch(urlBatch) ? urlBatch : storedBatch;
      if (!this.isValidBatch(batch)) return;
      if (this.isValidBatch(urlBatch)) this.clearCustomBatchFromUrl();
      this.batchId = batch;
      this.storeBatch(batch);
      this.pollFailures = 0;
      this.resetProgress();
      this.setView('progress');
      this.setProgress(5, this.isPredefined ? 'Restoring your Jobs preview...' : 'Restoring your Custom Tale preview...');
      this.poll();
    }

    async submit(event) {
      event.preventDefault();
      this.clearAlert();
      if (!this.photoFile) {
        this.markPhotoInvalid();
        this.showAlert('Please upload a child photo.');
        this.photoInput.focus();
        return;
      }
      if (!this.isPredefined) {
        const keywordCount = this.storyKeywords().length;
        if (keywordCount === 0) {
          if (this.storyKeywordsInput) this.storyKeywordsInput.setAttribute('aria-invalid', 'true');
          this.showAlert('Please add at least one story keyword.');
          if (this.storyKeywordsInput) this.storyKeywordsInput.focus();
          return;
        }
        if (keywordCount > 5) {
          this.showAlert('Please use no more than 5 story keywords.');
          if (this.storyKeywordsInput) this.storyKeywordsInput.focus();
          return;
        }
      }
      if (!this.form.reportValidity()) return;

      this.generateButton.disabled = true;
      this.pollFailures = 0;
      this.resetPreview();
      this.resetProgress();
      this.setView('progress');
      this.setProgress(4, this.isPredefined ? 'Preparing your personalized cover...' : 'Writing your story...');

      try {
        const formData = new FormData(this.form);
        formData.set('reference', this.photoFile, this.photoFile.name || 'child-photo.jpg');
        formData.set('image_provider', this.imageProvider);
        formData.set('image_model', this.imageModel);
        if (this.isPredefined) {
          const childName = String(formData.get('child_name') || 'The child').trim();
          const fixedBrief = String(formData.get('story_request') || '').replaceAll('{child_name}', childName);
          formData.set('story_request', fixedBrief);
          formData.set('product_slug', this.productSlug);
        } else {
          formData.delete('story_request');
          formData.delete('product_slug');
        }
        window.KiddoWonderFunnel?.emit('preview_started');
        this.publishAnalytics('custom_tale_started', {
          style: String(formData.get('style') || ''),
          language: String(formData.get('language') || ''),
          story_keyword_count: this.storyKeywords().length,
          image_provider: this.imageProvider,
          product_slug: this.productSlug,
        });
        const data = await this.fetchJson(this.apiUrl('/api/custom-tale/generate'), {
          method: 'POST',
          body: formData,
        });
        if (!this.isValidBatch(data.batch)) {
          const error = new Error('The preview service returned an invalid reference. Please try again.');
          error.retryable = false;
          throw error;
        }
        this.batchId = data.batch;
        this.storeBatch(this.batchId);
        this.setProgress(8, this.isPredefined ? 'Preparing your personalized cover...' : 'Writing your story...');
        this.poll();
      } catch (error) {
        this.generateButton.disabled = false;
        this.setView('form');
        this.showAlert(error.message);
      }
    }

    async poll() {
      window.clearTimeout(this.pollTimer);
      if (!this.batchId) return;
      const batch = this.batchId;
      try {
        const data = await this.fetchJson(this.apiUrl(`/api/custom-tale/${encodeURIComponent(batch)}`), {
          cache: 'no-store',
        });
        if (!batch || this.batchId !== batch) return;
        this.setProgress(data.progress, data.stage);
        this.setProgressSummary(data.loading_summary);
        if (data.status === 'error') {
          const error = new Error(data.error || 'Preview generation failed.');
          error.retryable = false;
          throw error;
        }
        if (data.cover_ready || data.cover_url) {
          window.KiddoWonderFunnel?.emit('preview_generated', batch);
          this.publishAnalyticsOnce('custom_tale_cover_ready', {
            pages_ready: Number(data.pages_ready || 0),
            pages_total: Number(data.pages_total || 3),
          });
          if (data.ready || data.status === 'ready') {
            this.publishAnalyticsOnce('custom_tale_preview_ready', {
              pages_total: Number(data.pages_total || 3),
            });
          }
          const signature = this.previewSignature(data);
          if (signature !== this.lastPreviewSignature) {
            await this.loadPreview(batch);
            this.lastPreviewSignature = signature;
          } else if (this.previewView.hidden) {
            this.setView('preview');
          }
          this.pollFailures = 0;
          if (data.ready || data.status === 'ready' || !data.pages_requested) return;
          this.pollTimer = window.setTimeout(() => this.poll(), 2500);
          return;
        }
        this.pollFailures = 0;
        this.pollTimer = window.setTimeout(() => this.poll(), 2500);
      } catch (error) {
        if (!batch || this.batchId !== batch) return;
        const canRetry = error.retryable !== false && this.pollFailures < this.maxPollFailures;
        if (canRetry) {
          this.pollFailures += 1;
          const retryDelay = Math.min(8000, 1500 * (2 ** (this.pollFailures - 1)));
          const reconnecting = `Connection interrupted. Reconnecting (${this.pollFailures}/${this.maxPollFailures})...`;
          if (!(this.previewData && this.previewData.cover && this.previewData.cover.image_url)) {
            this.setProgress(this.progressValue, reconnecting);
          }
          this.pollTimer = window.setTimeout(() => this.poll(), retryDelay);
          return;
        }
        const hasCover = Boolean(this.previewData && this.previewData.cover && this.previewData.cover.image_url);
        if (error.retryable === false && !hasCover) this.storeBatch('');
        this.generateButton.disabled = false;
        if (hasCover) {
          if (this.previewData) this.previewData.pages_requested = false;
          this.pagesGate.hidden = false;
          this.pagesPreview.hidden = true;
          this.pagesButton.disabled = false;
          this.pagesButton.textContent = 'Try the first pages again';
          this.setView('preview');
          this.setPreviewControls(false, true, false);
          this.regenerateButton.disabled = false;
        } else {
          this.setView('form');
        }
        this.showAlert(error.message);
      }
    }

    async loadPreview(batch) {
      const data = await this.fetchJson(
        this.apiUrl(`/api/custom-tale/${encodeURIComponent(batch)}/preview`),
        { cache: 'no-store' }
      );
      if (!batch || this.batchId !== batch) return;
      if (!(data.cover && data.cover.image_url)) {
        const error = new Error('Your cover is still being prepared.');
        error.retryable = true;
        throw error;
      }
      this.previewData = data;
      await this.renderPreview(data);
      this.generateButton.disabled = false;
    }

    loadPreviewImage(image, url, alt) {
      if (image.dataset.loadedUrl === url && image.complete && image.naturalWidth > 0) {
        image.hidden = false;
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        image.hidden = true;
        image.onload = () => {
          image.dataset.loadedUrl = url;
          image.hidden = false;
          resolve();
        };
        image.onerror = () => {
          image.hidden = true;
          image.removeAttribute('src');
          image.removeAttribute('data-loaded-url');
          const error = new Error('A preview image could not be loaded. Reconnecting...');
          error.retryable = true;
          reject(error);
        };
        image.alt = alt;
        image.src = url;
        if (image.complete && image.naturalWidth > 0) {
          image.hidden = false;
          resolve();
        }
      });
    }

    async renderPreview(data) {
      const genderLabel = data.child_gender === 'male' ? 'Boy' : data.child_gender === 'female' ? 'Girl' : '';
      const childDetails = [data.child_name, data.child_age ? `age ${data.child_age}` : '', genderLabel, this.isPredefined ? '' : data.style_label]
        .filter(Boolean)
        .join(' · ');
      this.root.querySelector('[data-preview-meta]').textContent = childDetails;
      const coverImage = this.root.querySelector('[data-cover-image]');
      this.setPreviewControls(false, true, Boolean(data.pages_requested));
      await this.loadPreviewImage(
        coverImage,
        data.cover.image_url,
        `${data.title || 'Custom Tale'} cover featuring ${data.child_name || 'the child'}`
      );

      // Reveal the cover immediately; ready pages can continue loading below it.
      this.setView('preview');
      window.KiddoWonderFunnel?.viewed(coverImage, this.batchId);
      this.pagesGate.hidden = Boolean(data.pages_requested);
      this.pagesPreview.hidden = !data.pages_requested;
      this.pagesButton.disabled = false;
      this.pagesButton.textContent = 'See the first pages';
      const pages = Array.isArray(data.pages) ? data.pages : [data.page_one].filter(Boolean);
      const pageLoads = [];
      for (let pageNumber = 1; pageNumber <= 3; pageNumber += 1) {
        const page = pages.find((item) => Number(item && item.page) === pageNumber) || {};
        const image = this.root.querySelector(`[data-page-image="${pageNumber}"]`);
        const loading = this.root.querySelector(`[data-page-loading="${pageNumber}"]`);
        const copy = this.root.querySelector(`[data-page-copy="${pageNumber}"]`);
        const card = this.root.querySelector(`[data-page-card="${pageNumber}"]`);
        this.root.querySelector(`[data-page-title="${pageNumber}"]`).textContent = page.title || '';
        this.root.querySelector(`[data-page-text="${pageNumber}"]`).textContent = page.text || '';

        if (page.ready && page.image_url) {
          pageLoads.push(
            this.loadPreviewImage(
              image,
              page.image_url,
              `Illustrated page ${pageNumber} featuring ${data.child_name || 'the child'}`
            ).then(() => {
              loading.hidden = true;
              copy.hidden = false;
              card.classList.add('is-ready');
            })
          );
        } else {
          image.hidden = true;
          loading.hidden = false;
          copy.hidden = true;
          card.classList.remove('is-ready');
        }
      }
      await Promise.all(pageLoads);
      this.setPreviewControls(Boolean(data.ready), true, Boolean(data.pages_requested));
    }

    async requestPreviewPages() {
      const url = this.apiUrl(`/api/custom-tale/${encodeURIComponent(this.batchId)}/pages`);
      let lastError;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          return await this.fetchJson(url, { method: 'POST' });
        } catch (error) {
          lastError = error;
          if (error.retryable === false || attempt === 3) throw error;
          this.pagesButton.textContent = `Reconnecting... (${attempt}/3)`;
          await new Promise((resolve) => window.setTimeout(resolve, 700 * attempt));
        }
      }
      throw lastError;
    }

    async generatePages() {
      if (!this.batchId || !this.previewData || !this.previewData.cover) return;
      this.clearAlert();
      this.pagesButton.disabled = true;
      this.pagesButton.textContent = 'Starting the pages...';
      this.pagesGate.hidden = true;
      this.pagesPreview.hidden = false;
      this.previewData.pages_requested = true;
      this.setPreviewControls(false, true, true);
      this.pollFailures = 0;
      try {
        await this.requestPreviewPages();
        this.lastPreviewSignature = '';
        this.poll();
      } catch (error) {
        this.previewData.pages_requested = false;
        this.pagesButton.disabled = false;
        this.pagesButton.textContent = 'See the first pages';
        this.pagesGate.hidden = false;
        this.pagesPreview.hidden = true;
        this.setPreviewControls(false, true, false);
        this.showAlert(error.message);
      }
    }

    async regenerate() {
      if (!this.batchId) return;
      this.clearAlert();
      const button = this.regenerateButton;
      button.disabled = true;
      this.pollFailures = 0;
      this.resetProgress();
      this.setView('progress');
      this.setProgress(5, 'Refreshing your cover...');
      try {
        await this.fetchJson(this.apiUrl(`/api/custom-tale/${encodeURIComponent(this.batchId)}/regenerate`), {
          method: 'POST',
        });
        this.resetPreview();
        this.setView('progress');
        button.disabled = false;
        this.poll();
      } catch (error) {
        button.disabled = false;
        this.setView('preview');
        this.showAlert(error.message);
      }
    }

    edit() {
      window.clearTimeout(this.pollTimer);
      this.storeBatch('');
      this.clearCustomBatchFromUrl();
      this.batchId = '';
      this.pollFailures = 0;
      this.resetPreview();
      this.clearAlert();
      this.setView('form');
    }

    getClientId() {
      if (this.clientId) return this.clientId;

      try {
        const existing = window.localStorage.getItem('kb_uid');
        const validExisting = /^[a-f0-9]{32}$/i.test(existing || '')
          || /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(existing || '');
        if (validExisting) {
          this.clientId = existing;
          return existing;
        }
      } catch (error) {
        // Continue with an in-memory ID when storage is unavailable.
      }

      const id = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, '')
        : this.randomHexId();
      try {
        window.localStorage.setItem('kb_uid', id);
      } catch (error) {
        // Private browsing or strict settings may block localStorage.
      }
      this.clientId = id;
      return id;
    }

    randomHexId() {
      if (window.crypto && window.crypto.getRandomValues) {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
      }
      return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    }

    shopifyRoute(path) {
      const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      return `${root.replace(/\/?$/, '/')}${String(path || '').replace(/^\//, '')}`;
    }

    async ensureSingleCustomTale(variantId) {
      let response;
      try {
        response = await fetch(this.shopifyRoute('cart.js'), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
      } catch (error) {
        throw new Error('Shopify could not check your cart. Please try again.');
      }
      const cart = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(cart.description || cart.message || 'Shopify could not check your cart.');
      }
      if (!Array.isArray(cart.items)) {
        throw new Error('Shopify returned an invalid cart response. Please try again.');
      }

      const hasCustomTale = cart.items.some((item) => {
        const properties = item.properties || {};
        return String(item.variant_id || item.id) === String(variantId)
          || item.handle === this.productSlug
          || properties['_Book Slug'] === this.productSlug
          || properties['Book Slug'] === this.productSlug;
      });
      if (hasCustomTale) {
        throw new Error(`Your cart already contains ${this.productTitle}. Please complete that order or remove it before adding another.`);
      }
    }

    async addToCart(coverType, button) {
      if (!this.previewData || !this.batchId) {
        this.showAlert('Generate the preview before adding this book to your cart.');
        return;
      }
      if (!(this.previewData.cover && this.previewData.cover.image_url)) {
        this.showAlert('Please wait until your personalized cover is ready before adding the book to your cart.');
        return;
      }
      if (this.isPredefined) {
        await this.purchaseProductPromise;
        if (this.purchaseProductError) {
          this.showAlert(`Shopify could not load ${this.productTitle}. Please refresh the page and try again.`);
          return;
        }
      }
      const variant = this.purchaseVariants[coverType];
      const variantId = String(variant && variant.id || '').trim();
      if (!variantId || variantId === '0' || variant.available === false) {
        this.showAlert(`The ${coverType === 'softcover' ? 'Softcover' : 'Hardcover'} option is not available yet.`);
        return;
      }

      const editButton = this.editButton;
      const regenerateButton = this.regenerateButton;
      this.purchaseButtons.forEach((purchaseButton) => { purchaseButton.disabled = true; });
      editButton.disabled = true;
      regenerateButton.disabled = true;
      const buttonAction = button.querySelector('[data-purchase-action]');
      if (buttonAction) buttonAction.textContent = 'Adding to cart...';
      this.clearAlert();

      try {
        const clientId = this.getClientId();
        const properties = {
          'Child Name': this.previewData.child_name || '',
          'Child Age': this.previewData.child_age || '',
          'Child Gender': this.previewData.child_gender || '',
          'Language': this.previewData.language || '',
          'Cover Type': coverType === 'softcover' ? 'Softcover' : 'Hardcover',
          '_Book Slug': this.previewData.product_slug || this.productSlug,
          '_Batch': this.batchId,
          '_KB UID': clientId,
          '_Cover Image': `${this.apiUrl(`/api/custom-tale/${encodeURIComponent(this.batchId)}/cover`)}?v=${Date.now()}`,
        };

        // The paid-order webhook reads the private technical properties from
        // this exact line item. Avoid public cart attributes and order notes.
        await this.ensureSingleCustomTale(variantId);

        const response = await fetch(this.shopifyRoute('cart/add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1, properties }],
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || `Shopify could not add ${this.productTitle} to the cart.`);
        }
        const addedItems = Array.isArray(result.items) ? result.items : [];
        const addedTale = addedItems.find((item) => {
          const properties = item.properties || {};
          return String(item.variant_id || item.id) === variantId
            && properties['_Batch'] === this.batchId
            && Number(item.quantity) === 1;
        });
        if (!addedTale) {
          throw new Error(`Shopify did not confirm the ${this.productTitle} cart item. Please try again.`);
        }
        this.publishAnalyticsOnce('custom_tale_added_to_cart', {
          variant_id: variantId,
          cover_type: coverType,
          pages_previewed: Array.isArray(this.previewData.pages)
            ? this.previewData.pages.filter((page) => page && page.ready).length
            : Number(this.previewData.pages_ready || 0),
          pages_requested: Boolean(this.previewData.pages_requested),
          product_slug: this.productSlug,
        });
        window.location.assign(this.cartUrl);
      } catch (error) {
        editButton.disabled = false;
        regenerateButton.disabled = false;
        this.setPreviewControls(
          Boolean(this.previewData && this.previewData.ready),
          Boolean(this.previewData && this.previewData.cover && this.previewData.cover.image_url),
          Boolean(this.previewData && this.previewData.pages_requested)
        );
        this.showAlert(error.message);
      }
    }
  }

  function initialize(scope) {
    const rootScope = scope || document;
    rootScope.querySelectorAll('[data-custom-tale-builder]').forEach((root) => {
      const requestedBook = (new URL(window.location.href).searchParams.get('book') || '').trim().toLowerCase();
      const isTogetherTale = Array.from(document.querySelectorAll('[data-together-story] option'))
        .some((option) => option.value === requestedBook);
      if (isTogetherTale) {
        root.hidden = true;
        return;
      }
      new KiddoWonderCustomTale(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
